import AddRoundedIcon from '@mui/icons-material/AddRounded';
import {
  Autocomplete, AutocompleteInputChangeReason, Box, Button, Divider, Paper, PaperProps, TextField,
} from '@mui/material';
import {
  CompositionEvent, Ref, UIEvent, useEffect, useMemo, useRef, useState,
} from 'react';
import { PlaceRef } from 'src/entities/Course.entity';
import { PlaceEntity } from 'src/entities/placeEntity';
import { useDebouncedValue } from 'src/hooks/useDebouncedValue';
import { PlaceSearchBy, usePlaceSearchQuery } from 'src/hooks/usePlaceSearch.query';
import PlaceFormDialog from 'src/pages/V2PlacesPresenter/formDialog';

type Props = {
  value: PlaceRef | null;
  onChange: (place: PlaceRef | null) => void;
  label: string;
  /** Why not autoFocus: jsx-a11y/no-autofocus が error のため、呼び出し側が ref で focus する */
  inputRef?: Ref<HTMLInputElement>;
  disabled?: boolean;
  /**
   * internal_location_code: 地点コードだけで探し、欄には選んだ地点の地点コードを出す（「配送先を追加」の地点コード欄）。
   * name: 名前だけで探す（同じく地点名欄）。address: 住所だけで探し、欄には住所を出す（同じく住所の欄、#384）。
   * 指定なしは名前か地点コード（積地の欄）
   */
  searchBy?: PlaceSearchBy;
  /** 欄の高さ。既定はコースマスタのダイアログに合わせた small。medium の欄に並べるときに渡す（#405） */
  size?: 'small' | 'medium';
  /**
   * 候補を開く▼を隠す。隣の名称・住所が freeSolo の Autocomplete（▼が出ない）のときに、
   * ここだけプルダウンに見えないようそろえる（#405）
   */
  hidePopupIcon?: boolean;
};

const SEARCH_HINT: Record<PlaceSearchBy | 'any', string> = {
  internal_location_code: '地点コードで検索', name: '名称で検索', address: '住所で検索', any: '名前か地点コードで検索',
};
/** 候補のリストの下端からこの距離に来たら次のページを頼む */
const LOAD_MORE_THRESHOLD_PX = 40;
/**
 * MUI に渡すラベルの末尾に、見えない区切りと地点の id を付ける（欄に出すときは外す）。
 * Why not 表示の文字のまま渡す: MUI は継ぎ足しの後にハイライトを「同じラベルの最初の候補」へ付け直すので、
 * 地点コード・住所・名前が同じ別の地点が前にあると、キーボードで選んでいた地点から移ってしまう
 */
const LABEL_ID_SEPARATOR = '\u2063';
/** MUI が候補のハイライトとスクロールを動かすキー */
const LIST_NAVIGATION_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End']);
const displayText = (muiLabel: string) => muiLabel.split(LABEL_ID_SEPARATOR)[0];

/**
 * 地点マスタから選ぶ（D21 決定 5）。名前・地点コードの部分一致を API に任せ（GET /api/v5/master/places）、
 * 候補の末尾の「地点を追加＋」で地点マスタの登録ダイアログ（V2PlacesPresenter/formDialog）を開き、
 * 登録した地点をそのまま選ぶ。
 * 欄を開いている間だけ検索し、何も打たなければ全地点を並べ、候補の下端までスクロールしたら次のページを継ぎ足す（#384）
 */
export default function PlaceAutocomplete({
  value, onChange, label, inputRef, disabled, searchBy, size = 'small', hidePopupIcon,
}: Props) {
  // 欄に出す文字（input）と検索する語（query）を分け、IME の変換中は query を進めない。
  // query は利用者が打った語だけ。選んだ値のラベル（MUI の reset）は欄に出すだけで検索しない（何も打たずに開けば全地点）
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // Why not useComposition（state）: 変換開始と同じ tick の change ではまだ false のことがあるので ref で同期的に見る（imeStringFilter と同じ）
  const composing = useRef(false);
  const [formOpen, setFormOpen] = useState(false);
  const debounced = useDebouncedValue(query, 300);
  // 語が落ち着くまで検索しない: 閉じた欄に打ち始めて開いた直後に、まだ空の debounced で全地点を取りに行かない
  const search = usePlaceSearchQuery(debounced, searchBy, open && debounced === query);
  const optionLabel = (o: PlaceRef) => {
    if (searchBy === 'internal_location_code') return o.internalLocationCode ?? '';
    return searchBy === 'address' ? o.address : o.name;
  };
  // 一度継ぎ足しを頼んだ後の候補のリストと、利用者がスクロールした位置。描画のたびにこの位置へ戻す（語を変えた・閉じたら捨てる）
  const appendScroll = useRef<{ el: HTMLElement; top: number } | null>(null);
  const changeInput = (muiLabel: string, reason: AutocompleteInputChangeReason) => {
    const next = displayText(muiLabel);
    appendScroll.current = null;
    setInput(next);
    // 打った語（input）と × で消した（clear）ときだけ進める。選んだ値のラベル（selectOption・reset・blur など）は検索語にしない
    if (reason !== 'input' && reason !== 'clear') setQuery('');
    else if (!composing.current) setQuery(next);
  };
  // Why not !isFetchingNextPage: 取り直し中は isFetchingNextPage が false のまま、fetchNextPage が取り直しを取り消してしまう
  const loadMore = (e: UIEvent<HTMLElement>) => {
    const el = e.currentTarget;
    // MUI が位置を 0 にした分のスクロールは、下の effect が戻した後に届くので、ここで読むのは利用者の位置になる
    if (appendScroll.current) appendScroll.current.top = el.scrollTop;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > LOAD_MORE_THRESHOLD_PX) return;
    if (!search.hasNextPage || search.isFetching) return;
    appendScroll.current = { el, top: el.scrollTop };
    search.fetchNextPage();
  };
  // 閉じたら、閉じた欄の地点検索のキャッシュを捨てる。
  // Why not onClose の中で捨てる: その時点ではこの欄の検索はまだ enabled で、閉じた欄として捨てられない（閉じた後の描画で enabled が外れる）
  const wasOpen = useRef(false);
  const { reset } = search;
  useEffect(() => {
    if (wasOpen.current && !open) reset();
    wasOpen.current = open;
  }, [open, reset]);
  // Why not MUI に任せる: 選んだ値が候補に無いと、MUI は候補が変わった描画とその次の描画で位置を 0 に戻す（取得の始まり・継ぎ足しの後）。
  // Why not 取得が終わったら捨てる: 継ぎ足しの次の描画でも戻されるので、利用者がスクロールした位置を覚え続けて戻す。
  // MUI の戻しは子（Autocomplete）の effect で走るので、親のこの effect が毎回の描画の後に位置を戻せる
  // 検索条件が変わった（IME の確定など changeInput を通らない経路も含む）ら、新しい候補の先頭から見せるので覚えた位置を捨てる。
  // 下の戻す effect より先に宣言する（同じ描画では宣言順に走る）
  useEffect(() => {
    appendScroll.current = null;
  }, [query, searchBy]);
  useEffect(() => {
    const saved = appendScroll.current;
    if (saved && saved.el.scrollTop !== saved.top) saved.el.scrollTop = saved.top;
  });
  // 確定の値が compositionend と一緒に来るブラウザもあるので、ここで欄と検索語の両方を確定の値にそろえる
  const endComposition = (e: CompositionEvent<HTMLDivElement>) => {
    composing.current = false;
    const { value } = e.target as HTMLInputElement;
    setInput(value);
    setQuery(value);
  };
  // Why not 毎レンダーで Paper を作る: 候補リストが再マウントされてスクロールが戻るので 1 回だけ作り、ハンドラは ref で渡す
  const openForm = useRef(() => {});
  openForm.current = () => setFormOpen(true);
  const PaperWithAdd = useMemo(() => function PaperWithAdd(props: PaperProps) {
    return (
      <Paper {...props}>
        {props.children}
        <Divider />
        <Button
          fullWidth
          size="small"
          startIcon={<AddRoundedIcon />}
          sx={{ justifyContent: 'flex-start' }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => openForm.current()}
        >
          地点を追加＋
        </Button>
      </Paper>
    );
  }, []);

  return (
    <>
      <Autocomplete
        options={search.places}
        value={value}
        inputValue={input}
        onInputChange={(_e, next, reason) => changeInput(next, reason)}
        onChange={(_e, next) => {
          // 打った語と同じ文字の候補を選ぶと、MUI は欄の文字が変わらないので onInputChange を呼ばない。選んだら検索語を空にする
          setQuery('');
          onChange(next);
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          appendScroll.current = null;
          setOpen(false);
        }}
        forcePopupIcon={hidePopupIcon ? false : 'auto'}
        filterOptions={(options) => options}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionLabel={(o) => `${optionLabel(o)}${LABEL_ID_SEPARATOR}${o.id}`}
        loading={search.isFetching}
        loadingText="読み込み中…"
        disabled={disabled}
        noOptionsText="該当する地点がありません"
        slots={{ paper: PaperWithAdd }}
        slotProps={{ listbox: { onScroll: loadMore } }}
        // キーボードの移動は MUI がキーの処理の中で位置を動かし、scroll の通知は後から届く。その間の描画で戻さないよう覚えた位置を捨てる
        // （MUI はハイライトした候補を保つので、この後は位置を 0 に戻さない）
        onKeyDown={(e) => {
          if (LIST_NAVIGATION_KEYS.has(e.key)) appendScroll.current = null;
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Box sx={{ fontSize: 14 }}>
                {option.name}
                {option.internalLocationCode && <Box component="span" sx={{ ml: 1, fontSize: 12, color: 'text.secondary' }}>{option.internalLocationCode}</Box>}
              </Box>
              <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{option.address}</Box>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={SEARCH_HINT[searchBy ?? 'any']}
            size={size}
            inputRef={inputRef}
            onCompositionStart={() => { composing.current = true; }}
            onCompositionEnd={endComposition}
            // 変換中に欄を離れて compositionend が来ないと、次の入力から検索が止まったままになるので離れたら戻す
            onBlur={() => { composing.current = false; }}
          />
        )}
      />
      {formOpen && (
        <PlaceFormDialog
          dialogIsOpen={formOpen}
          toggleDialogIsOpen={() => setFormOpen(false)}
          lockCompany
          onSaved={(place: PlaceEntity) => {
            onChange({
              id: place.id, internalLocationCode: place.internal_location_code ?? null, name: place.name, address: place.address,
            });
            changeInput(optionLabel({
              id: place.id, internalLocationCode: place.internal_location_code ?? null, name: place.name, address: place.address,
            }), 'reset');
          }}
        />
      )}
    </>
  );
}
